<script>
import { mask } from 'vue-the-mask'
import { validationMixin } from 'vuelidate'
import { required, maxLength, email } from 'vuelidate/lib/validators'
import { phoneNumber, rules } from '@/utils/validators'

import UploadImage from '@/components/UploadImage'
import GoogleAddressAutocomplete from '@/components/GoogleAddressAutocomplete'
import googleMapsApi from '@/mixins/googleMapsApi'

import {
  GET_VIEWER_QUERY,
  IS_EMAIL_AVAILABLE_QUERY
} from '@/graph/gql/user.gql'
import { updateViewer } from '@/graph/mutations/user'

export default {
  name: 'SettingsView',
  directives: { mask },
  components: { UploadImage, GoogleAddressAutocomplete },
  mixins: [validationMixin, googleMapsApi],
  apollo: {
    user: {
      query: GET_VIEWER_QUERY,
      fetchPolicy: 'cache-and-network',
      update: data => data.viewer,
      result({ data }) {
        // Prefill form
        if (data) this.updateForm(data.viewer)
      },
      skip() {
        return !!this.user
      }
    },
    isEmailAvailable: {
      query: IS_EMAIL_AVAILABLE_QUERY,
      variables() {
        return { email: this.form.email }
      },
      skip() {
        const sameEmail = this.user && this.user.email === this.form.email
        return this.$v.form.email.$invalid || sameEmail
      }
    }
  },
  data() {
    return {
      rules,
      form: {
        avatar: null,
        description: '',
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        sellerLocation: null,
        pickupInstructions: ''
      },
      submitting: false,

      uploadedFile: null
    }
  },
  computed: {
    isSeller() {
      // TODO: rework
      return this.user && this.user.roles ? this.user.roles.seller : false
    }
  },
  methods: {
    onFileChange({ src, file }) {
      this.uploadedFile = file
      this.form.avatar = src
    },

    updateForm(data) {
      if (!data) return

      const {
        avatar,
        description,
        firstName,
        lastName,
        email,
        phone,
        sellerLocation,
        pickupInstructions
      } = data

      this.form = {
        avatar,
        description,
        firstName,
        lastName,
        email,
        phone,
        sellerLocation: sellerLocation || {},
        pickupInstructions
      }
    },

    submit() {
      if (this.submitting || this.$v.$invalid) return
      if (this.$apollo.loading || this.isEmailAvailable === false) return
      this.submitting = true

      const { __typename, ...sellerLocation } = this.form.sellerLocation
      const data = {
        ...this.form,
        avatar: this.uploadedFile,
        sellerLocation
      }

      updateViewer(data)
        .then(() => this.$toast('Settings updated'))
        .finally(() => (this.submitting = false))
    }
  },
  validations: {
    form: {
      avatar: { required },
      description: {
        required,
        maxLength: maxLength(180)
      },
      firstName: { required },
      lastName: { required },
      email: { required, email },
      phone: { required, phoneNumber },
      sellerLocation: {
        name: { required },
        place_id: { required },
        latitude: { required },
        longitude: { required }
      }
    }
  }
}
</script>

<template>
  <transition name="slide-up" appear>
    <!-- Settings page -->
    <div class="view-grid">
      <div>
        <div class="display-1 font-weight-bold mb-5">
          Settings
        </div>

        <div>
          <v-skeleton-loader
            :loading="$apollo.queries.user.loading"
            transition="fade-transition"
            height="94"
            type="list-item-avatar-two-line"
          >
            <form clas="new-item-form" @submit.prevent="submit()">
              <div
                v-if="$isSeller && !$apollo.queries.user.loading"
                class="d-flex align-center mt-3"
              >
                <UploadImage
                  :default-src="form.avatar"
                  rounded
                  @input="onFileChange"
                />

                <span class="caption secondary--text ml-6">
                  Best profile photos convey <br />
                  authenticity and trust
                </span>
              </div>

              <v-textarea
                v-if="$isSeller"
                v-model="form.description"
                :rules="[rules.required]"
                validate-on-blur
                name="story"
                label="Your story"
                counter
                maxlength="180"
                rows="2"
                auto-grow
                class="mt-6"
              />

              <v-text-field
                v-model="form.firstName"
                :rules="[rules.required]"
                validate-on-blur
                label="First name"
              />

              <v-text-field
                v-model="form.lastName"
                :rules="[rules.required]"
                validate-on-blur
                label="Last name"
              />

              <v-text-field
                v-model="form.email"
                :rules="[rules.required, rules.email]"
                :loading="$apollo.queries.isEmailAvailable.loading"
                :error-messages="
                  $v.form.email.$invalid || isEmailAvailable !== false
                    ? []
                    : ['Email already in use.']
                "
                validate-on-blur
                label="Email"
                type="email"
              />

              <v-text-field
                v-model="form.phone"
                v-mask="'+### #### ########'"
                readonly
                :rules="[rules.required, rules.phone]"
                validate-on-blur
                label="Phone"
                type="tel"
              />

              <div v-if="$isSeller && form.sellerLocation">
                <GoogleAddressAutocomplete
                  v-if="mapsApiLoaded"
                  v-model="form.sellerLocation"
                  label="Your address"
                  required
                />

                <v-text-field
                  v-model.trim="form.pickupInstructions"
                  class="secondary--text"
                  label="Pickup instructions"
                />
              </div>

              <!-- Save changes -->
              <v-btn
                :loading="submitting"
                :disabled="$v.$invalid || isEmailAvailable === false"
                type="submit"
                rounded
                depressed
                color="primary"
                class="my-6 px-6"
              >
                Save
              </v-btn>
            </form>
          </v-skeleton-loader>
        </div>
      </div>
      <div class="content-bottom">
        <!-- Delete accoun -->
        <v-btn
          v-if="!$apollo.queries.user.loading"
          to="/delete-account"
          outlined
          depressed
          rounded
          color="red"
        >
          Delete account
        </v-btn>
      </div>
    </div>
  </transition>
</template>
